import type { ReactNode } from "react";
import type { ColumnDef } from "@tanstack/react-table";
import { Checkbox, SortableHeader } from "web-design-system";
import { ArrowUpWideNarrowIcon } from "lucide-react";
import { format } from "date-fns";
import capitalize from "lodash/capitalize";
import type { PlanStatus, SIPPlan } from "creso-core/types";
import { AmcLogo } from "../../logos";
import { PlanStatusBadge } from "../../badges";
import { Amount } from "../../primitives";

export type SipColumnOptions = {
  /** Adds the leading multi-select checkbox column for bulk actions. */
  selectable?: boolean;
  /** Row action menu. Omit it and no action column is produced. */
  renderActions?: (plan: SIPPlan) => ReactNode;
};

export function createSipColumns({
  selectable,
  renderActions,
}: SipColumnOptions = {}): ColumnDef<SIPPlan>[] {
  const columns: ColumnDef<SIPPlan>[] = [];

  if (selectable) {
    columns.push({
      id: "select-col",
      size: 20,
      header: ({ table }) => {
        const { rows } = table.getCoreRowModel();
        const allSelected =
          rows.length > 0 && rows.every((r) => r.getIsSelected());
        const someSelected = rows.some((r) => r.getIsSelected());
        return (
          <Checkbox
            className="mt-1"
            checked={
              allSelected ? true : someSelected ? "indeterminate" : false
            }
            onCheckedChange={(checked) =>
              rows.forEach((row) => row.toggleSelected(checked === true))
            }
          />
        );
      },
      cell: ({ row }) => (
        <Checkbox
          className="mt-1"
          checked={row.getIsSelected()}
          disabled={!row.getCanSelect()}
          onCheckedChange={row.getToggleSelectedHandler()}
        />
      ),
    });
  }

  columns.push(
    {
      id: "scheme_name",
      header: "Scheme name",
      accessorKey: "scheme_name",
      minSize: 250,
      cell: (info) => (
        <div className="flex flex-row gap-2 items-center">
          <AmcLogo amc_code={info.row.original.amc_morningstar_code} />
          <span className="text-sm">{info.row.original.scheme_name}</span>
        </div>
      ),
      enableColumnFilter: true,
      filterFn: "includesString",
    },
    {
      id: "status",
      // asc = Active -> Paused -> Cancelled
      header: ({ column }) => (
        <SortableHeader column={column} ascFirst>
          Status
        </SortableHeader>
      ),
      accessorKey: "status",
      cell: (info) => (
        <PlanStatusBadge status={info.getValue() as PlanStatus} />
      ),
    },
    {
      header: "Next Installment",
      accessorKey: "installment_due_date",
      cell: (info) => {
        if (!info.getValue() || info.row.original.status === "CANCELLED")
          return "-";
        return <span>{format(info.getValue() as string, "dd MMM yyyy")}</span>;
      },
    },
    {
      header: "Frequency",
      accessorKey: "frequency",
      cell: (info) => (
        <span className="text-sm">{capitalize(info.getValue() as string)}</span>
      ),
    },
    {
      header: "Amount",
      accessorKey: "installment_amount",
      cell: ({ row }) => (
        <div className="flex flex-col gap-1">
          <Amount precision={0}>{row.original.installment_amount}</Amount>
          {row.original.step_up_frequency && (
            <div className="flex flex-row gap-0.5 text-muted-foreground text-xs">
              <ArrowUpWideNarrowIcon className="size-4" />
              Step-up
            </div>
          )}
        </div>
      ),
    }
  );

  if (renderActions) {
    columns.push({
      id: "actions",
      cell: (info) => (
        <div className="text-right">{renderActions(info.row.original)}</div>
      ),
      minSize: 50,
      size: 50,
    });
  }

  return columns;
}
